import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { toast } from 'react-toastify';
import { listUnits, deleteUnit } from '../../services/unidades';
import ConfirmDialog from '../common/ConfirmDialog';
import SettingsMenuButton from '../common/SettingsMenuButton';
import { useAuthSession } from '../../contexts/AuthSessionContext';

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(8, 145, 178, 0.08), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.06), transparent 45%),
  radial-gradient(ellipse at 50% 100%, rgba(255, 199, 90, 0.04), transparent 50%),
  linear-gradient(180deg, #f7f9ff 0%, #ffffff 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow:
    '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  overflow: 'hidden',
  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
};

const SECTION_ICON_BOX_SX = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'linear-gradient(135deg, rgba(8, 145, 178, 0.14) 0%, rgba(124, 58, 237, 0.10) 100%)',
  color: '#0891b2',
  flexShrink: 0,
  border: '1px solid rgba(8, 145, 178, 0.18)',
};

const SETTINGS_BUTTON_SX = {
  width: 44,
  height: 44,
  borderRadius: '12px',
  color: '#475569',
  background: 'rgba(15, 23, 42, 0.04)',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  transition: 'all 0.25s ease',
  '& svg': { transition: 'transform 0.5s ease' },
  '&:hover': {
    background: 'rgba(8, 145, 178, 0.08)',
    borderColor: 'rgba(8, 145, 178, 0.24)',
    color: '#0891b2',
  },
  '&:hover svg': { transform: 'rotate(45deg)' },
};

const PRIMARY_BUTTON_SX = {
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.95rem',
  px: 3,
  py: 1.25,
  background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  boxShadow: '0 10px 24px -8px rgba(8, 145, 178, 0.45)',
  transition:
    'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #0e7490 0%, #155e75 100%)',
    boxShadow: '0 14px 32px -8px rgba(8, 145, 178, 0.6)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
};

const UnitsPage = () => {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { isAuthenticated, isHydrating, isOwner } = useAuthSession();

  useEffect(() => {
    if (isHydrating) return;

    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    if (!isOwner) {
      toast.error('Você não tem permissão para acessar as unidades.');
      navigate('/products', { replace: true });
    }
  }, [isAuthenticated, isHydrating, isOwner, navigate]);

  const loadUnits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listUnits();
      setUnits(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Não foi possível carregar as unidades.');
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isHydrating || !isOwner) return;
    loadUnits();
  }, [isHydrating, isOwner, loadUnits]);

  const handleDelete = async () => {
    try {
      await deleteUnit(deleteId);
      toast.success('Unidade removida com sucesso.');
      setDeleteId(null);
      loadUnits();
    } catch (err) {
      toast.error(err?.message || 'Erro ao remover a unidade. Verifique se ela não está em uso.');
    }
  };

  if (isHydrating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: PAGE_BG,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#0891b2' }} />
      </Box>
    );
  }

  if (!isOwner) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 4, md: 6 } }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2 }}>
        <Stack spacing={3}>
          <Card sx={{ ...CARD_SX, position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background:
                  'linear-gradient(90deg, #06b6d4 0%, #8b5cf6 50%, #f59e0b 100%)',
              }}
            />
            <CardContent sx={{ pt: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/products')}
                  sx={{
                    color: '#475569',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 1.25,
                    py: 0.6,
                    borderRadius: '10px',
                    '&:hover': {
                      background: 'rgba(8, 145, 178, 0.08)',
                      color: '#0891b2',
                    },
                  }}
                >
                  Voltar para produtos
                </Button>
                <SettingsMenuButton buttonProps={{ sx: SETTINGS_BUTTON_SX }} />
              </Stack>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                gap={2}
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      ...SECTION_ICON_BOX_SX,
                      width: 52,
                      height: 52,
                      borderRadius: '16px',
                    }}
                  >
                    <StorefrontOutlinedIcon sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography
                      component="h1"
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        letterSpacing: '-0.02em',
                        color: '#0f172a',
                        mb: 0.25,
                      }}
                    >
                      Unidades
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 720 }}>
                      Gerencie as unidades disponíveis para todos os produtos da suíte.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/unidades/novo')}
                  sx={{
                    ...PRIMARY_BUTTON_SX,
                    alignSelf: { xs: 'stretch', md: 'auto' },
                    minWidth: { md: 180 },
                  }}
                >
                  Nova unidade
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={CARD_SX}>
            <CardContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Stack spacing={1.5}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={56}
                      sx={{ borderRadius: '12px' }}
                    />
                  ))}
                </Stack>
              ) : units.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Box
                    sx={{
                      ...SECTION_ICON_BOX_SX,
                      width: 64,
                      height: 64,
                      borderRadius: '16px',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <StorefrontOutlinedIcon sx={{ fontSize: 30 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: '#0f172a', mb: 0.5, letterSpacing: '-0.01em' }}
                  >
                    Nenhuma unidade cadastrada
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#64748b', mb: 3, maxWidth: 380, mx: 'auto' }}
                  >
                    Adicione sua primeira unidade para começar a gerenciar os produtos da sua operação.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/unidades/novo')}
                    sx={PRIMARY_BUTTON_SX}
                  >
                    Adicionar primeira unidade
                  </Button>
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto', mx: -2, px: 2 }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow
                        sx={{
                          '& .MuiTableCell-root': {
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: '#475569',
                            background: 'rgba(15, 23, 42, 0.025)',
                            borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                            py: 1.5,
                          },
                          '& .MuiTableCell-root:first-of-type': {
                            borderTopLeftRadius: '12px',
                            borderBottomLeftRadius: '12px',
                          },
                          '& .MuiTableCell-root:last-of-type': {
                            borderTopRightRadius: '12px',
                            borderBottomRightRadius: '12px',
                          },
                        }}
                      >
                        <TableCell>Nome</TableCell>
                        <TableCell>Cidade / UF</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody
                      sx={{
                        '& .MuiTableRow-root': {
                          transition: 'background 0.15s ease',
                        },
                        '& .MuiTableRow-root:hover': {
                          background: 'rgba(8, 145, 178, 0.04)',
                        },
                        '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                          borderBottom: 'none',
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                          py: 1.75,
                        },
                      }}
                    >
                      {units.map((unit) => (
                        <TableRow key={unit.unidade_id}>
                          <TableCell>
                            <Typography
                              fontWeight={600}
                              sx={{ color: '#0f172a', fontSize: '0.95rem' }}
                            >
                              {unit.nome}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                              {unit.cidade || '—'}
                              {unit.estado ? ` / ${unit.estado}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={unit.ativo ? 'Ativo' : 'Inativo'}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.72rem',
                                letterSpacing: '0.02em',
                                borderRadius: '8px',
                                ...(unit.ativo
                                  ? {
                                      background:
                                        'linear-gradient(135deg, rgba(16,185,129,0.16), rgba(5,150,105,0.10))',
                                      color: '#047857',
                                      border: '1px solid rgba(16,185,129,0.32)',
                                    }
                                  : {
                                      background: 'rgba(15,23,42,0.04)',
                                      color: '#64748b',
                                      border: '1px solid rgba(15,23,42,0.10)',
                                    }),
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Editar" arrow>
                              <IconButton
                                onClick={() => navigate(`/unidades/${unit.unidade_id}`)}
                                aria-label={`Editar unidade ${unit.nome}`}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '10px',
                                  color: '#0891b2',
                                  background: 'rgba(8, 145, 178, 0.06)',
                                  transition: 'all 0.2s ease',
                                  mr: 0.5,
                                  '&:hover': {
                                    background: 'rgba(8, 145, 178, 0.14)',
                                    transform: 'translateY(-1px)',
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir" arrow>
                              <IconButton
                                onClick={() => setDeleteId(unit.unidade_id)}
                                aria-label={`Excluir unidade ${unit.nome}`}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '10px',
                                  color: '#dc2626',
                                  background: 'rgba(220, 38, 38, 0.06)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    background: 'rgba(220, 38, 38, 0.14)',
                                    transform: 'translateY(-1px)',
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Stack>

        <ConfirmDialog
          open={Boolean(deleteId)}
          title="Excluir unidade"
          message="Tem certeza que deseja excluir esta unidade? Esta ação não pode ser desfeita."
          onConfirm={handleDelete}
          onClose={() => setDeleteId(null)}
          confirmText="Excluir"
        />
      </Box>
    </Box>
  );
};

export default UnitsPage;
